/**
 * ChatWindow — orquestra chrome, lista, rodapé, DnD de mídia e menu de contexto.
 */
import React, { useCallback, useRef } from 'react';
import type { ChatConversation } from '../hooks/useChat';
import type {
    ContactCardInput,
    LocationMessageInput,
    PixMessageInput,
} from '../hooks/useChatSpecialActions';
import { ChatEmptyState } from './chat-window';
import { MessageList } from './chat-window/MessageList';
import { MessageContextMenu } from './chat-window/MessageContextMenu';
import { ChatWindowChrome } from './chat-window/ChatWindowChrome';
import { ChatWindowFooter } from './chat-window/ChatWindowFooter';
import { useChatScroll } from '../hooks/useChatScroll';
import { useChatGroupedMessages } from '../hooks/useChatGroupedMessages';
import { useChatUIInteractions } from '../hooks/useChatUIInteractions';
import { useChatAttachFlow } from '../hooks/useChatAttachFlow';
import { useChatFileDrop } from '../hooks/useChatFileDrop';
import { useAuth } from '../../../contexts/AuthContext';
import { useChatContext } from '../../../contexts/useChatContext';
import { isOfficialWhatsappConnection } from '../hooks/useChatConnection';
import { isMessageEditable } from './chat-window/useMessageCaption';

const CHAT_WINDOW_CONTAINER_CLASSES =
    'relative flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden bg-chat-window-bg font-sans text-base';

export interface ChatWindowProps {
    conversation: ChatConversation | undefined;
    onSendMessage: (text: string, quotedMessage?: { id: string; sender: string; text?: string }) => boolean | void;
    onEditMessage?: (messageId: string, newText: string, originalText?: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    onSendMedia?: (file: File, caption?: string) => void;
    onSendContact?: (input: ContactCardInput) => Promise<void>;
    onSendLocation?: (input: LocationMessageInput) => Promise<void>;
    onSendPix?: (input: PixMessageInput) => Promise<void>;
    onAssignToMe?: () => void;
    onTransfer?: (userId: string) => void;
    onCloseConversation?: () => void;
    onReopenConversation?: () => void;
    currentUserId?: string | null;
    isPopup?: boolean;
    isLoadingMessages?: boolean;
    selectedTagIds?: string[];
    onBack?: () => void;
    isContactPanelOpen?: boolean;
    onToggleContactPanel?: () => void;
    onLoadOlderMessages?: () => Promise<boolean>;
    isLoadingOlder?: boolean;
    hasMoreMessages?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
    conversation, onSendMessage, onEditMessage, onDeleteMessage, onSendMedia,
    onSendContact, onSendLocation, onSendPix, onAssignToMe, onTransfer,
    onCloseConversation, onReopenConversation, currentUserId, isPopup,
    isLoadingMessages, selectedTagIds = [], onBack, isContactPanelOpen,
    onToggleContactPanel, onLoadOlderMessages, isLoadingOlder, hasMoreMessages,
}) => {
    const { user } = useAuth();
    const { connections, selectedConnection, isPeerTyping, sendTyping } = useChatContext();
    const chatPaneRef = useRef<HTMLElement>(null);

    const isInstagramConversation = Boolean(
        conversation?.platform === 'instagram' || conversation?.id?.startsWith('ig:'),
    );
    // IG nunca herda Cloud API do seletor WhatsApp (edit/delete/affordances errados).
    const officialApi = !isInstagramConversation && isOfficialWhatsappConnection(
        connections,
        selectedConnection,
        conversation?.connectionId,
    );

    const conversationConnection = connections.find((c) => c.instanceName === conversation?.instanceName);
    const myAvatar =
        conversationConnection?.profilePhotoUrl
        || (!isInstagramConversation ? selectedConnection?.profilePhotoUrl : undefined)
        || user?.user_metadata?.avatar_url;

    const ui = useChatUIInteractions({
        onSendMessage, onEditMessage, onDeleteMessage, conversationId: conversation?.id,
    });

    // Mesmo fluxo WA/IG: staging → preview → sendMedia (IG já roteia no hook de mídia).
    const attach = useChatAttachFlow({
        mediaOnly: isInstagramConversation,
        onSendMedia: async (file, caption) => {
            if (onSendMedia) await onSendMedia(file, caption);
        },
    });

    const absorbComposerTextAsCaption = useCallback(() => {
        const pending = ui.messageText.trim();
        if (!pending) return;
        if (!attach.caption.trim()) attach.setCaption(pending);
        ui.setMessageText('');
    }, [attach.caption, attach.setCaption, ui.messageText, ui.setMessageText]);

    const handleIncomingFiles = useCallback((files: File[]) => {
        absorbComposerTextAsCaption();
        attach.stageIncomingFiles(files);
    }, [absorbComposerTextAsCaption, attach]);

    const { isDragging, dropHandlers } = useChatFileDrop({
        enabled: Boolean(conversation && onSendMedia && !isPopup),
        onFiles: handleIncomingFiles,
    });

    const setMessageTextWithTyping = useCallback((text: string) => {
        ui.setMessageText(text);
        if (text.trim()) sendTyping?.();
    }, [ui.setMessageText, sendTyping]);

    const handleScrollToTop = useCallback(async () => {
        if (onLoadOlderMessages && hasMoreMessages && !isLoadingOlder) {
            await onLoadOlderMessages();
        }
    }, [onLoadOlderMessages, hasMoreMessages, isLoadingOlder]);

    const { messagesEndRef, scrollContainerRef, handleScroll, scrollToMessage } = useChatScroll(conversation, {
        onScrollToTop: handleScrollToTop, scrollThreshold: 150, isLoadingOlder,
    });
    const messagesWithDateSeparators = useChatGroupedMessages(conversation);
    const showAssignBanner = Boolean(
        conversation && onAssignToMe && !isPopup && conversation.assignedTo !== currentUserId,
    );

    if (!conversation) {
        return (
            <section className={CHAT_WINDOW_CONTAINER_CLASSES} aria-label="Janela de chat">
                <ChatEmptyState />
            </section>
        );
    }

    const chatPlatform = conversation.platform ?? 'whatsapp';
    const contextMenu = ui.contextMenu;
    const canEditMessage = !officialApi && contextMenu ? isMessageEditable(contextMenu.message) : false;

    return (
        <section
            className={CHAT_WINDOW_CONTAINER_CLASSES}
            aria-label={`Conversa com ${conversation.name}`}
            {...dropHandlers}
        >
            {!isPopup && (
                <ChatWindowChrome
                    conversation={conversation}
                    onTransfer={onTransfer}
                    onCloseConversation={onCloseConversation}
                    onReopenConversation={onReopenConversation}
                    isSearchOpen={ui.isSearchOpen}
                    selectedTagIds={selectedTagIds}
                    onBack={onBack}
                    onToggleSearch={ui.handleToggleSearch}
                    isContactPanelOpen={isContactPanelOpen}
                    onToggleContactPanel={onToggleContactPanel}
                    onNavigateToMessage={scrollToMessage}
                    onCloseSearch={ui.handleCloseSearch}
                    onHighlightChange={ui.handleHighlightChange}
                />
            )}

            <main id="chat-pane-body" ref={chatPaneRef} className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <MessageList
                    messages={messagesWithDateSeparators}
                    conversationName={conversation.name}
                    conversationAvatar={conversation.avatar}
                    myAvatar={myAvatar}
                    chatPlatform={chatPlatform}
                    isLoading={isLoadingMessages}
                    isPopup={isPopup}
                    scrollContainerRef={scrollContainerRef}
                    messagesEndRef={messagesEndRef}
                    onScroll={handleScroll}
                    onScrollToMessage={scrollToMessage}
                    onContextMenu={ui.handleContextMenu}
                    highlightedMessageId={ui.highlightedMessageId}
                    isLoadingOlder={isLoadingOlder}
                    hasMoreMessages={hasMoreMessages}
                />
                {isDragging && (
                    <div
                        className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed border-primary bg-primary/10"
                        aria-hidden
                    >
                        <p className="rounded-lg bg-card px-4 py-2 text-sm font-medium text-default shadow-sm">
                            Solte para anexar
                        </p>
                    </div>
                )}
            </main>

            <ChatWindowFooter
                conversationName={conversation.name}
                chatPlatform={chatPlatform}
                showAssignBanner={showAssignBanner}
                onAssignToMe={onAssignToMe}
                replyingTo={ui.replyingTo}
                onCancelReply={() => ui.setReplyingTo(null)}
                editingMessage={ui.editingMessage}
                onCancelEdit={() => { ui.setEditingMessage(null); ui.setMessageText(''); }}
                isPeerTyping={isPeerTyping}
                messageText={ui.messageText}
                setMessageText={setMessageTextWithTyping}
                sendValidationError={ui.sendValidationError}
                handleSend={ui.handleSend}
                attach={attach}
                absorbComposerTextAsCaption={absorbComposerTextAsCaption}
                onSendMedia={onSendMedia}
                onSendContact={onSendContact}
                onSendLocation={onSendLocation}
                onSendPix={onSendPix}
                isPopup={isPopup}
            />

            {contextMenu && (
                <MessageContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onReply={() => ui.handleReply(contextMenu.message)}
                    onEdit={() => ui.handleEdit(contextMenu.message)}
                    onDelete={() => ui.handleDelete(contextMenu.message)}
                    onClose={() => ui.setContextMenu(null)}
                    canEdit={canEditMessage}
                    canDelete={!officialApi && contextMenu.message.sender === 'me'}
                    boundaryRef={chatPaneRef}
                    preferLeft={contextMenu.message.sender === 'me'}
                />
            )}
        </section>
    );
};

export default ChatWindow;
